/**
 * Cookie-consent state shared by the banner, the PostHog and GA4 mounts, and the
 * "manage preferences" control on the cookies page. The choice is stored in a
 * first-party cookie (the consent record itself is a strictly-necessary
 * cookie, exempt from consent — it is not analytics). Client-only helpers.
 */

export const CONSENT_COOKIE = "lh.cookieConsent";

/** Window event the cookies-page control dispatches to reopen the banner. */
export const CONSENT_OPEN_EVENT = "lh:consent-open";

/** Window event fired after the stored choice changes, so any mounted reader
 *  (the banner's `useSyncExternalStore`) re-reads the cookie. */
export const CONSENT_CHANGED_EVENT = "lh:consent-changed";

/**
 * PostHog ingestion host. The portal query only exposes the project key
 * (`posthogId`), not the region, so default to EU cloud (this is an EU
 * portal) and allow an env override for forks on US cloud or a reverse proxy.
 */
export const DEFAULT_POSTHOG_HOST = "https://eu.i.posthog.com";

export type Consent = "granted" | "denied";

/** The stored choice, or `null` when the visitor hasn't decided yet (→ show
 *  the banner). Returns `null` on the server. */
export function readConsent(): Consent | null {
  if (typeof document === "undefined") return null;
  for (const part of document.cookie.split(";")) {
    const [key, value] = part.split("=");
    if (key?.trim() === CONSENT_COOKIE) {
      return value === "granted" || value === "denied" ? value : null;
    }
  }
  return null;
}

/** Persist the choice (1-year, lax) — same attributes as the UI-pref cookies. */
export function writeConsent(value: Consent) {
  document.cookie = `${CONSENT_COOKIE}=${value};path=/;max-age=31536000;samesite=lax`;
}
